import "chartjs-adapter-date-fns";

import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Decimation,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import tw from "twin.macro";

import { useOneModuleBatteryDataSuspense } from "@/api/modules-data/postLog/queries";
import { BatteryData } from "@/api/modules-data/postLog/types";
import { Card } from "@/components/card";
import { usePostDetailContext } from "@/hooks/usePostDetailContext";

ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  Decimation
);

type ChartConfig = {
  label: string;
  unit: string;
  color: string;
  getValue: (data: BatteryData) => number;
  formatValue: (val: number) => string;
};

const PostMonitoring = () => {
  const { post } = usePostDetailContext();
  const { data: modulesBatteryData } = useOneModuleBatteryDataSuspense({
    address: post.address,
  });

  const labels = modulesBatteryData.map((data) => new Date(data.createdAt));

  const charts: ChartConfig[] = [
    {
      label: "Tension",
      unit: "V",
      color: "#0ea5e9", // sky-500
      getValue: (data) => data.voltage / 1000, // mV -> V
      formatValue: (val) => `${val.toFixed(3)} V`,
    },
    {
      label: "Capacité",
      unit: "mAh",
      color: "#8b5cf6", // violet-500
      getValue: (data) => data.capacity,
      formatValue: (val) => `${val.toFixed(0)} mAh`,
    },
    {
      label: "Courant",
      unit: "mA",
      color: "#f59e0b", // amber-500
      getValue: (data) => data.current,
      formatValue: (val) => `${val.toFixed(0)} mA`,
    },
    {
      label: "État de charge",
      unit: "%",
      color: "#16a34a", // green-600
      getValue: (data) => data.stateOfCharge,
      formatValue: (val) => `${val.toFixed(0)} %`,
    },
  ];

  const createChartData = (config: ChartConfig) => ({
    labels,
    datasets: [
      {
        label: `${config.label} (${config.unit})`,
        data: modulesBatteryData.map(config.getValue),
        borderColor: config.color,
        backgroundColor: `${config.color}33`, // Opacity 20%
        tension: 0.25,
        pointRadius: 0,
        borderWidth: 2,
        fill: true,
      },
    ],
  });

  const createChartOptions = (config: ChartConfig): ChartOptions<"line"> => ({
    responsive: true,
    maintainAspectRatio: false,
    normalized: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        intersect: false,
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y;
            if (val === null) return `${config.label}: N/A`;
            return `${config.label}: ${config.formatValue(val)}`;
          },
        },
      },
      decimation: {
        enabled: true,
        algorithm: "lttb",
        samples: 150,
      },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "minute",
          displayFormats: { minute: "HH:mm" },
          tooltipFormat: "HH:mm:ss",
        },
        ticks: { maxRotation: 0, autoSkip: true },
        grid: { display: false },
      },
      y: {
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: `${config.label} (${config.unit})`,
        },
        grid: { color: "rgba(0,0,0,0.075)" },
      },
    },
  });

  if (!modulesBatteryData.length) {
    return (
      <div tw="flex items-center justify-center">
        <div tw="text-sm text-gray-500">Aucune donnée batterie</div>
      </div>
    );
  }

  const Grid = tw.div`grid max-w-[580px] xl:grid-cols-2 xl:max-w-none grid-cols-1 gap-4`;

  return (
    <Grid>
      {charts.map((config) => (
        <Card key={config.label}>
          <Line
            data={createChartData(config)}
            options={createChartOptions(config)}
          />
        </Card>
      ))}
    </Grid>
  );
};

export default PostMonitoring;
